import type { ScreenBase } from "@frui.ts/screens/dist";
import { Router } from "@frui.ts/screens/dist";
import { Modules } from "config";
import AccountBalanceCheckRequestResponse from "entities/accountBalanceCheckRequestResponse";
import AccountBalanceCheckRequestsPermittedActionsResponse from "entities/accountBalanceCheckRequestsPermittedActionsResponse";
import SearchAccountBalanceCheckRequestsRequest from "entities/searchAccountBalanceCheckRequestsRequest";
import AccountBalanceCheckRequestStatus from "entities/accountBalanceCheckRequestStatus";
import { action, computed, observable, reaction } from "mobx";
import AccountingBalanceRepository from "repositories/accountingBalanceRepository";
import EventBus, { subLedgerReportGenerated } from "services/eventBus";
import LocalizationService from "services/localizationService";
import MonthOptionsService from "services/monthOptionsService";
import NotificationService from "services/notificationService";
import SecurityService from "services/securityService";
import ListViewModel from "viewModels/listViewModel";
import BalanceCreateViewModel from "./balanceCreateViewModel";
import BalanceDetailViewModel from "./balanceDetailViewModel";
import { attachManualValidator, addError, removeError, getValidationMessage } from "@frui.ts/validation";
import Month from "entities/month";
import MonthOfYearRangeResponse from "entities/monthOfYearRangeResponse";
import camelCase from "lodash/camelCase";
import download from "downloadjs";
import { watchBusy } from "@frui.ts/screens";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";

const navigationName = "account-balance";

export enum BalanceAction {
  DETAIL_ACCESS = "DetailAccess",
  CHECK_RESULT_DOWNLOAD = "CheckResultDownload",
}

@Router.registerRoute({ route: navigationName, name: Router.Self, children: [BalanceCreateViewModel, BalanceDetailViewModel] })
export default class BalanceViewModel extends ListViewModel<
  AccountBalanceCheckRequestResponse,
  ScreenBase,
  BalanceCreateViewModel,
  SearchAccountBalanceCheckRequestsRequest
> {
  readonly modelName = "balance";
  @observable toMonth?: Date;
  @observable fromMonth?: Date;
  @observable permittedActions?: AccountBalanceCheckRequestsPermittedActionsResponse;
  private isFirstActivation = true;

  constructor(
    protected createViewModelFactory: ReturnType<typeof BalanceCreateViewModel.Factory>,
    protected detailViewModel: ReturnType<typeof BalanceDetailViewModel.Factory>,
    protected eventBus: EventBus,
    protected repository: AccountingBalanceRepository,
    protected monthOptionsService: MonthOptionsService,
    protected securityService: SecurityService,
    protected notificationService: NotificationService,
    protected localizationService: LocalizationService
  ) {
    super();
    this.navigationName = navigationName;
  }

  onInitialize(): Promise<any> | void {
    attachManualValidator(this, true);

    this.unsubscriptions.push(
      this.eventBus.subscribe(subLedgerReportGenerated, this.loadData),
      reaction(
        () => [this.toMonth],
        () => {
          this.setToMonth();
          this.validateMonthRange();
        }
      ),
      reaction(
        () => [this.fromMonth],
        () => {
          this.setFromMonth();
          this.validateMonthRange();
        }
      )
    );
    return super.onInitialize();
  }

  protected onActivate(): Promise<void> {
    if (this.isFirstActivation) {
      this.isFirstActivation = false;
    } else {
      this.loadData();
    }
    return super.onActivate();
  }

  @computed
  get navigationParams() {
    return {
      offset: this.pagingFilter.offset,
    };
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowRead() {
    return false;
  }

  @action.bound
  allowToDetailAccess(item: AccountBalanceCheckRequestResponse) {
    return item.permittedActions.checkRequestDetailAccess;
  }

  @computed
  get months() {
    return this.monthOptionsService.startOfMonthOptions.map(option => ({
      ...option,
      label: option.label.replace(/^(\d{4}) - (\d{2})$/, "$2/$1"),
    }));
  }

  @computed
  get toSelectedMonth() {
    if (this.toMonth) {
      return new Month(this.toMonth);
    }
    return undefined;
  }

  @computed
  get fromSelectedMonth() {
    if (this.fromMonth) {
      return new Month(this.fromMonth);
    }
    return undefined;
  }

  @computed
  get hasFilterChanges() {
    return !!(this.fromMonth ?? this.toMonth);
  }

  @computed
  get isFilterValid() {
    return !this.fromMonthError && !this.toMonthError && this.hasFilterChanges;
  }

  @computed
  get fromMonthError() {
    return getValidationMessage(this, "fromMonth");
  }

  @computed
  get toMonthError() {
    return getValidationMessage(this, "toMonth");
  }

  @action.bound
  actions(item: AccountBalanceCheckRequestResponse) {
    return Object.values(BalanceAction).filter(action => this.allowToAction(action, item));
  }

  @action.bound
  allowToAction(action: BalanceAction, item: AccountBalanceCheckRequestResponse) {
    switch (action) {
      case BalanceAction.DETAIL_ACCESS:
        return this.allowToDetailAccess(item);
      case BalanceAction.CHECK_RESULT_DOWNLOAD:
        return this.allowToCheckResultDownload(item);
      default:
        return false;
    }
  }

  @action.bound
  allowToCheckResultDownload(item: AccountBalanceCheckRequestResponse) {
    return item.permittedActions.checkResultDownload;
  }

  @action.bound
  onDetailAccessClick(item: AccountBalanceCheckRequestResponse) {
    const detailViewModel = this.detailViewModel;
    if (detailViewModel) {
      this.tryActivateChild(detailViewModel(item.id));
    }
  }

  @action.bound
  @watchBusy
  async onCheckResultDownloadClick(item: AccountBalanceCheckRequestResponse) {
    const fileResponse = await this.repository.getResultFile(item.id);
    const resultFileNamePrefix = this.localizationService.translateModule(Modules.finance, "inventory.balance.resultFileName");
    const defaultFileName = `${resultFileNamePrefix} ${item.accountingPeriodTo.replace("-", "")}.xlsx`;
    download(fileResponse.data, parseFileNameFromAxiosResponse(fileResponse, defaultFileName));
  }

  disabledActionTooltip(action: BalanceAction, checkRequest: AccountBalanceCheckRequestResponse) {
    let act: { rejectionReason?: string; isAllowed?: boolean } | undefined;
    switch (action) {
      case BalanceAction.DETAIL_ACCESS:
        act = checkRequest.permittedActions.checkRequestDetailAccess;
        break;
      case BalanceAction.CHECK_RESULT_DOWNLOAD:
        act = checkRequest.permittedActions.checkResultDownload;
        break;
      default:
        act = undefined;
        break;
    }
    return act?.rejectionReason ? this.translate(`actions.${camelCase(action)}.${act.rejectionReason}`) : undefined;
  }

  @action.bound
  getStatusTextClass(status: AccountBalanceCheckRequestStatus): string {
    switch (status) {
      case AccountBalanceCheckRequestStatus.ToBeProcessed:
        return "text-warning";
      case AccountBalanceCheckRequestStatus.Processed:
        return "text-success";
      case AccountBalanceCheckRequestStatus.ProcessingFailed:
        return "text-danger";
      default:
        return "text-muted";
    }
  }

  @computed
  get nextAvailablePeriod(): string | undefined {
    const processedItems = this.items
      .filter(item => item.status === AccountBalanceCheckRequestStatus.Processed)
      .sort((a, b) => {
        return b.accountingPeriodTo.localeCompare(a.accountingPeriodTo);
      });

    if (processedItems.length === 0) {
      return undefined;
    }

    const latestProcessed = processedItems[0];
    const parts = latestProcessed.accountingPeriodTo.split("-");
    const year = Number(parts[0]);
    const month = Number(parts[1]);

    let nextMonth = month + 1;
    let nextYear = year;

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear = nextYear + 1;
    }

    return `${nextYear}-${nextMonth.toString().padStart(2, "0")}`;
  }

  @action.bound
  onAddClick() {
    const createViewModelFactory = this.createViewModelFactory;
    if (createViewModelFactory && this.allowWrite) {
      return () => {
        const fromMonth = localStorage.getItem("balance_nextAvailablePeriod") ?? this.nextAvailablePeriod;
        return this.tryActivateChild(
          createViewModelFactory(this.permittedActions?.checkRequestCreation.latestAccountingPeriodTo, fromMonth)
        );
      };
    } else {
      return undefined;
    }
  }

  protected createFilter(): SearchAccountBalanceCheckRequestsRequest {
    return new SearchAccountBalanceCheckRequestsRequest();
  }

  protected async fetchData() {
    const response = await this.repository.search(this.appliedFilter, this.pagingFilter);
    this.permittedActions = response.permittedActions;

    if (response.permittedActions?.checkRequestCreation?.latestAccountingPeriodTo) {
      localStorage.setItem(
        "balance_latestAccountingPeriodTo",
        response.permittedActions.checkRequestCreation.latestAccountingPeriodTo
      );
    }

    const processedItems = response.items
      .filter(item => item.status === AccountBalanceCheckRequestStatus.Processed)
      .sort((a, b) => b.accountingPeriodTo.localeCompare(a.accountingPeriodTo));

    if (processedItems.length > 0) {
      const latestProcessed = processedItems[0];
      const parts = latestProcessed.accountingPeriodTo.split("-");
      const year = Number(parts[0]);
      const month = Number(parts[1]);

      let nextMonth = month + 1;
      let nextYear = year;

      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear = nextYear + 1;
      }

      const nextPeriod = `${nextYear}-${nextMonth.toString().padStart(2, "0")}`;
      localStorage.setItem("balance_nextAvailablePeriod", nextPeriod);
    } else {
      localStorage.removeItem("balance_nextAvailablePeriod");
    }

    return {
      items: response.items,
      metadata: response.metadata,
    };
  }

  @action.bound
  private setFromMonth() {
    if (!this.filter.accountingPeriodToMonthRange) {
      this.filter.accountingPeriodToMonthRange = new MonthOfYearRangeResponse();
    }
    this.filter.accountingPeriodToMonthRange.from = this.fromSelectedMonth
      ? `${this.fromSelectedMonth.year}-${this.fromSelectedMonth.month.toString().padStart(2, "0")}`
      : undefined;
  }

  @action.bound
  private setToMonth() {
    if (!this.filter.accountingPeriodToMonthRange) {
      this.filter.accountingPeriodToMonthRange = new MonthOfYearRangeResponse();
    }
    this.filter.accountingPeriodToMonthRange.to = this.toSelectedMonth
      ? `${this.toSelectedMonth.year}-${this.toSelectedMonth.month.toString().padStart(2, "0")}`
      : undefined;
  }

  @action.bound
  validateMonthRange() {
    removeError(this, "fromMonth");
    removeError(this, "toMonth");

    if (this.fromMonth && this.toMonth && this.toMonth < this.fromMonth) {
      addError(this, "toMonth", this.localizationService.translate("validators.customMessages.afterSinceDate"));
    }
  }

  @action.bound
  resetFilterAndLoad() {
    this.fromMonth = undefined;
    this.toMonth = undefined;

    const result = super.resetFilterAndLoad();

    if (this.filter.accountingPeriodToMonthRange) {
      this.filter.accountingPeriodToMonthRange.from = undefined;
      this.filter.accountingPeriodToMonthRange.to = undefined;
    }

    return result;
  }

  get locale() {
    return this.localizationService.locale;
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.balance.${code}`, size);
  }

  translateStatus(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.finance, `inventory.balance.statusTypes.${code}`, size);
  }

  translateAttribute(code: string, size?: number) {
    return this.localizationService.translateAttribute("documentsInventory", code, size);
  }

  translateGeneral(code: string, size?: number) {
    return this.localizationService.translateGeneral(code, size);
  }
}
